import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from 'react';
import { getUserEntity } from 'common/UserEntityProvider';
import { AuthContext, AuthStatus } from 'components/authentication/context/authContext';
import { useApi, useApiData } from 'api/ApiProvider';
import Api from 'axiosApi/api';

// Definición de tipos para mensajes WebSocket
export interface WebSocketMessage {
  action: string;
  data?: any;
}

// Definición de estados de conexión
export enum WebSocketConnectionState {
  Connecting = 'Connecting',
  Connected = 'Connected',
  Disconnecting = 'Disconnecting',
  Disconnected = 'Disconnected',
  Reconnecting = 'Reconnecting',
}

// Define la interfaz del contexto WebSocket
interface WebSocketContextType {
  connection: WebSocket | null;
  connectionState: WebSocketConnectionState;
  sendMessage: (message: WebSocketMessage) => boolean;
  subscribe: <T>(eventName: string, callback: (data: T) => void) => () => void;
  unsubscribe: (eventName: string, callback: (data: any) => void) => void;
  reconnect: () => void;
}

// Crear el contexto con valores predeterminados
const WebSocketContext = createContext<WebSocketContextType>({
  connection: null,
  connectionState: WebSocketConnectionState.Disconnected,
  sendMessage: () => false,
  subscribe: () => () => {},
  unsubscribe: () => {},
  reconnect: () => {},
});

interface WebSocketProviderProps {
  children: ReactNode;
}

const RECONNECT_INTERVALS = [0, 2000, 5000, 10000, 20000];
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const api: Api = useApi();
  const { urlWssNotifications } = useApiData();
  
  const [connection, setConnection] = useState<WebSocket | null>(null);
  const [connectionState, setConnectionState] = useState<WebSocketConnectionState>(
    WebSocketConnectionState.Disconnected
  );
  const [eventHandlers, setEventHandlers] = useState<Record<string, Array<(data: any) => void>>>(
    {}
  );
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);

  const { authStatus } = useContext(AuthContext);
  const userEntity = getUserEntity();

  // Ref para manejar el timeout de reconexión
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref para prevenir múltiples conexiones simultáneas
  const connectionAttemptRef = useRef<boolean>(false);

  // Ref para almacenar la conexión actual y evitar closures obsoletos
  const connectionRef = useRef<WebSocket | null>(null);

  // Use ref to store current event handlers to avoid stale closure
  const eventHandlersRef = useRef<Record<string, Array<(data: any) => void>>>({});
  useEffect(() => {
    eventHandlersRef.current = eventHandlers;
  }, [eventHandlers]);

  // Sincronizar connectionRef con connection state
  useEffect(() => {
    connectionRef.current = connection;
  }, [connection]);

  // Limpiar timeout de reconexión
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Manejar la reconexión automática
  const scheduleReconnect = useCallback(() => {
    if (isReconnecting || authStatus !== AuthStatus.SignedIn || connectionAttemptRef.current) {
      return;
    }

    if (reconnectAttempt >= RECONNECT_INTERVALS.length) {
      console.warn('Max reconnection attempts reached. Not reconnecting.');
      setIsReconnecting(false);
      return;
    }

    setIsReconnecting(true);
    setConnectionState(WebSocketConnectionState.Reconnecting);

    const delay = RECONNECT_INTERVALS[reconnectAttempt];

    if (import.meta.env.DEV) {
      console.log(
        `Scheduling reconnection in ${delay}ms (${reconnectAttempt + 1}/${RECONNECT_INTERVALS.length})...`
      );
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      setReconnectAttempt((prev) => prev + 1);
      setIsReconnecting(false);
      // La reconexión se manejará en el useEffect principal
    }, delay);
  }, [reconnectAttempt, isReconnecting, authStatus]);

  // Función para crear una nueva conexión WebSocket
  const createConnection = useCallback(() => {
    if (!urlWssNotifications || authStatus !== AuthStatus.SignedIn || !userEntity?.id) {
      return null;
    }

    // Prevenir múltiples conexiones simultáneas
    if (connectionAttemptRef.current) {
      console.log('Connection attempt already in progress, skipping...');
      return null;
    }

    // Cerrar conexión existente si existe
    if (connectionRef.current && connectionRef.current.readyState !== WebSocket.CLOSED) {
      console.log('Closing existing connection before creating new one...');
      connectionRef.current.close(1000, 'Creating new connection');
    }

    connectionAttemptRef.current = true;

    if (import.meta.env.DEV) {
      console.log('Creating WebSocket connection...');
    }

    clearReconnectTimeout();
    setConnectionState(WebSocketConnectionState.Connecting);

    const urlWithParams = new URL(urlWssNotifications);
    urlWithParams.searchParams.append('userEntity', userEntity.id.toString());
    urlWithParams.searchParams.append('origin', window.location.origin);
    urlWithParams.searchParams.append(
      'CognitoUserSession',
      sessionStorage.getItem('CognitoUserSession')
    );

    if (import.meta.env.DEV) {
      console.log('WebSocketURL:', urlWithParams);
    }
    const ws = new WebSocket(urlWithParams.toString());

    ws.onopen = () => {
      // Verificar que esta sigue siendo la conexión actual
      if (connectionRef.current === ws) {
        setConnectionState(WebSocketConnectionState.Connected);
        setReconnectAttempt(0);
        setIsReconnecting(false);
        connectionAttemptRef.current = false;
        console.log('WebSocket connection established', connectionState);
      } else {
        // Esta conexión ya no es la actual, cerrarla
        console.log('Closing obsolete connection...');
        ws.close(1000, 'Obsolete connection');
      }
    };

    ws.onmessage = (event) => {
      // Solo procesar mensajes si esta es la conexión actual
      if (connectionRef.current !== ws) {
        return;
      }

      try {
        const message = JSON.parse(event.data);
        const eventName = message.action || 'message';

        if (eventHandlersRef.current[eventName]) {
          eventHandlersRef.current[eventName].forEach((handler) => {
            handler(message.data || message);
          });
        }

        if (import.meta.env.DEV) {
          console.log('WebSocket message received:', message);
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      connectionAttemptRef.current = false;
    };

    ws.onclose = (event) => {
      // Solo manejar el cierre si esta es la conexión actual
      if (connectionRef.current === ws) {
        // Add URL construction with query parameters for disconnect event

        setConnectionState(WebSocketConnectionState.Disconnected);
        setConnection(null);
        const urlWithUserEntityQueryParam = new URL(urlWssNotifications);
        urlWithUserEntityQueryParam.searchParams.append('userEntity', userEntity.id.toString());
        urlWithUserEntityQueryParam.searchParams.append('origin', window.location.origin);
        connectionAttemptRef.current = false;

        if (import.meta.env.DEV) {
          console.log('WebSocket connection closed:', event.code, event.reason);
          console.log('Disconnect URL:', urlWithUserEntityQueryParam.toString());
        }

        // Solo reconectar si estamos autenticados y no fue un cierre manual (código 1000)
        if (authStatus === AuthStatus.SignedIn && event.code !== 1000 && !isReconnecting) {
          console.log('WebSocket connection lost, scheduling reconnection...');
          scheduleReconnect();
        }
      }
    };

    return ws;
  }, [authStatus, userEntity?.id, clearReconnectTimeout, scheduleReconnect, isReconnecting]);

  // Efecto principal para manejar la conexión
  useEffect(() => {
    if (
      authStatus === AuthStatus.SignedIn &&
      !connection &&
      !isReconnecting &&
      !connectionAttemptRef.current
    ) {
      const newConnection = createConnection();
      if (newConnection) {
        setConnection(newConnection);
      }
    } else if (authStatus !== AuthStatus.SignedIn) {
      // Limpiar todo al desautenticarse
      clearReconnectTimeout();
      setIsReconnecting(false);
      setReconnectAttempt(0);
      connectionAttemptRef.current = false;
      if (connection) {
        setConnectionState(WebSocketConnectionState.Disconnecting);
        connection.close(1000, 'User signed out');
        setConnection(null);
      }
    }
  }, [authStatus, connection, isReconnecting, createConnection, clearReconnectTimeout]);

  // Forzar reconexión manualmente
  const reconnect = useCallback(() => {
    clearReconnectTimeout();
    setIsReconnecting(false);
    setReconnectAttempt(0);
    connectionAttemptRef.current = false;

    if (connection) {
      setConnectionState(WebSocketConnectionState.Disconnecting);
      connection.close(1000, 'Manual reconnection');
    } else {
      // Si no hay conexión, crear una nueva directamente
      const newConnection = createConnection();
      if (newConnection) {
        setConnection(newConnection);
      }
    }
  }, [connection, createConnection, clearReconnectTimeout]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      clearReconnectTimeout();
      connectionAttemptRef.current = false;
      if (connectionRef.current) {
        connectionRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [clearReconnectTimeout]);

  // Enviar un mensaje al servidor
  const sendMessage = useCallback(
    (message: WebSocketMessage): boolean => {
      if (!connection || connection.readyState !== WebSocket.OPEN) {
        console.warn('Cannot send message. WebSocket connection is not open.');
        return false;
      }

      try {
        connection.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
        return false;
      }
    },
    [connection]
  );

  // Suscribirse a un tipo de evento
  const subscribe = useCallback(<T,>(eventName: string, callback: (data: T) => void) => {
    console.log(`Subscribing to event: ${eventName}`);

    setEventHandlers((prev) => {
      const handlers = prev[eventName] || [];
      const newHandlers = {
        ...prev,
        [eventName]: [...handlers, callback as any],
      };
      console.log(`Updated handlers for ${eventName}:`, newHandlers[eventName].length);
      return newHandlers;
    });

    // Return unsubscribe function
    return () => {
      console.log(`Unsubscribing from event: ${eventName}`);
      setEventHandlers((prev) => {
        const handlers = prev[eventName] || [];
        const filteredHandlers = handlers.filter((handler) => handler !== callback);

        if (filteredHandlers.length === 0) {
          const { [eventName]: _, ...rest } = prev;
          return rest;
        }

        return {
          ...prev,
          [eventName]: filteredHandlers,
        };
      });
    };
  }, []);

  // Cancelar suscripción a un tipo de evento
  const unsubscribe = useCallback((eventName: string, callback: (data: any) => void) => {
    console.log(`Unsubscribing specific handler from event: ${eventName}`);

    setEventHandlers((prev) => {
      const handlers = prev[eventName] || [];
      const filteredHandlers = handlers.filter((handler) => handler !== callback);

      if (filteredHandlers.length === 0) {
        const { [eventName]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [eventName]: filteredHandlers,
      };
    });
  }, []);

  const value = {
    connection,
    connectionState,
    sendMessage,
    subscribe,
    unsubscribe,
    reconnect,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

// Hook personalizado para usar el contexto WebSocket
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);

  if (context === undefined) {
    throw new Error('useWebSocket debe ser usado dentro de WebSocketProvider');
  }

  return context;
};
